/**
 * Analytics Dashboard — Estimate metrics, conversion rates, margin & wastage trends
 */
import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { BarChart3, TrendingUp, Percent, Package, IndianRupee, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

function MiniBar({ data, maxVal, color }) {
  if (!data || data.length === 0) return null;
  const max = maxVal || Math.max(...data.map(d => d.value), 1);
  return (
    <div className="flex items-end gap-[3px] h-16">
      {data.map((d, i) => (
        <div key={i} className="flex flex-col items-center flex-1 min-w-0">
          <div className={`w-full rounded-t ${color}`} style={{ height: `${(d.value / max) * 100}%`, minHeight: d.value > 0 ? 4 : 0 }} />
          <span className="text-[8px] text-stone-400 mt-0.5 truncate w-full text-center">{d.label}</span>
        </div>
      ))}
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, color, trend }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3 flex items-start gap-3">
        <div className={`p-2 rounded-lg ${color}`}><Icon size={18} /></div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase font-bold text-stone-500 tracking-wide">{label}</p>
          <p className="text-xl font-extrabold text-stone-900">{value}</p>
          {sub && <p className="text-xs text-stone-500 mt-0.5">{sub}</p>}
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-0.5 text-xs font-bold ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
            {Math.abs(trend).toFixed(0)}%
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AnalyticsDashboard() {
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/estimates`, { headers: { Authorization: `Bearer ${token}` } });
        setEstimates(res.data || []);
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  const metrics = useMemo(() => {
    if (estimates.length === 0) return null;
    const total = estimates.length;
    const converted = estimates.filter(e => e.status === 'converted').length;
    const drafts = estimates.filter(e => e.status === 'draft').length;
    const conversionRate = total > 0 ? (converted / total) * 100 : 0;
    const totalRevenue = estimates.reduce((s, e) => s + (e.total_cost || 0), 0);
    const avgValue = total > 0 ? totalRevenue / total : 0;

    // Margin trends from calculations
    const margins = estimates.filter(e => e.calculations?.margin).map(e => ({
      value: e.calculations.margin,
      total: e.total_cost || 1,
      pct: e.total_cost ? (e.calculations.margin / e.total_cost) * 100 : 0,
    }));
    const avgMargin = margins.length > 0 ? margins.reduce((s, m) => s + m.pct, 0) / margins.length : 0;

    // Wastage from material summaries
    const wastages = [];
    estimates.forEach(e => {
      (e.material_summary || []).forEach(m => {
        if (m.wastage !== undefined) wastages.push({ material: m.name, wastage: m.wastage });
      });
    });
    const avgWastage = wastages.length > 0 ? wastages.reduce((s, w) => s + w.wastage, 0) / wastages.length : 0;

    // Material usage aggregation
    const materialUsage = {};
    estimates.forEach(e => {
      (e.material_summary || []).forEach(m => {
        const name = (m.name || '').replace(/\(.*\)/, '').trim();
        if (!materialUsage[name]) materialUsage[name] = { sheets: 0, cost: 0 };
        materialUsage[name].sheets += m.sheets || 0;
        materialUsage[name].cost += m.total_cost || 0;
      });
    });
    const topMaterials = Object.entries(materialUsage)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.sheets - a.sheets)
      .slice(0, 6);

    // Monthly volume (last 6 months)
    const monthlyVolume = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = d.toISOString().slice(0, 7);
      const monthLabel = d.toLocaleString('default', { month: 'short' });
      const count = estimates.filter(e => (e.created_at || '').startsWith(monthKey)).length;
      const revenue = estimates.filter(e => (e.created_at || '').startsWith(monthKey)).reduce((s, e) => s + (e.total_cost || 0), 0);
      monthlyVolume.push({ label: monthLabel, count, revenue });
    }

    // Wastage by material type
    const wastageByMat = {};
    wastages.forEach(w => {
      const key = (w.material || '').replace(/\(.*\)/, '').trim().slice(0, 12);
      if (!wastageByMat[key]) wastageByMat[key] = [];
      wastageByMat[key].push(w.wastage);
    });
    const wastageChart = Object.entries(wastageByMat).map(([mat, vals]) => ({
      label: mat, value: vals.reduce((s, v) => s + v, 0) / vals.length,
    }));

    return {
      total, converted, drafts, conversionRate, totalRevenue, avgValue,
      avgMargin, avgWastage, topMaterials, monthlyVolume, wastageChart,
    };
  }, [estimates]);

  if (loading) return <div className="text-center py-20 text-stone-500">Loading analytics...</div>;

  const m = metrics || { total: 0, converted: 0, drafts: 0, conversionRate: 0, totalRevenue: 0, avgValue: 0, avgMargin: 0, avgWastage: 0, topMaterials: [], monthlyVolume: [], wastageChart: [] };
  const fmt = v => `Rs ${Math.round(v).toLocaleString('en-IN')}`;

  return (
    <div className="space-y-5" data-testid="analytics-dashboard">
      <div className="flex items-center gap-2"><BarChart3 size={18} className="text-indigo-600" /><h3 className="font-bold text-stone-800">Analytics Dashboard</h3>
        <span className="text-xs bg-stone-100 px-2 py-0.5 rounded-full">{m.total} estimates</span>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Total Estimates" value={m.total} sub={`${m.converted} converted, ${m.drafts} draft`} icon={Package} color="bg-blue-100 text-blue-700" />
        <StatCard label="Conversion Rate" value={`${m.conversionRate.toFixed(0)}%`} sub={`${m.converted} of ${m.total}`} icon={Percent} color="bg-green-100 text-green-700" />
        <StatCard label="Total Revenue" value={fmt(m.totalRevenue)} sub={`Avg: ${fmt(m.avgValue)}`} icon={IndianRupee} color="bg-amber-100 text-amber-700" />
        <StatCard label="Avg Margin" value={`${m.avgMargin.toFixed(1)}%`} sub={`Wastage: ${m.avgWastage.toFixed(1)}%`} icon={TrendingUp} color="bg-violet-100 text-violet-700" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Monthly Volume */}
        <Card>
          <CardContent className="pt-4 pb-3">
            <h4 className="font-semibold text-sm text-stone-700 mb-3">Monthly Estimate Volume</h4>
            <MiniBar data={m.monthlyVolume.map(v => ({ label: v.label, value: v.count }))} color="bg-blue-500" />
          </CardContent>
        </Card>

        {/* Monthly Revenue */}
        <Card>
          <CardContent className="pt-4 pb-3">
            <h4 className="font-semibold text-sm text-stone-700 mb-3">Monthly Revenue</h4>
            <MiniBar data={m.monthlyVolume.map(v => ({ label: v.label, value: v.revenue }))} color="bg-emerald-500" />
          </CardContent>
        </Card>

        {/* Top Materials */}
        <Card>
          <CardContent className="pt-4 pb-3">
            <h4 className="font-semibold text-sm text-stone-700 mb-3">Top Materials (by Sheets)</h4>
            {m.topMaterials.length > 0 ? (
              <div className="space-y-2">
                {m.topMaterials.map((mat, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-xs text-stone-600 w-28 truncate">{mat.name}</span>
                    <div className="flex-1 bg-stone-100 rounded-full h-3 overflow-hidden">
                      <div className="bg-blue-500 h-full rounded-full" style={{ width: `${(mat.sheets / (m.topMaterials[0]?.sheets || 1)) * 100}%` }} />
                    </div>
                    <span className="text-xs font-bold text-stone-700 w-10 text-right">{mat.sheets}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-stone-400">No material data yet</p>}
          </CardContent>
        </Card>

        {/* Wastage by Material */}
        <Card>
          <CardContent className="pt-4 pb-3">
            <h4 className="font-semibold text-sm text-stone-700 mb-3">Avg Wastage by Material</h4>
            <MiniBar data={m.wastageChart} color="bg-red-400" maxVal={100} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
